import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';
import { Search, User, UserPlus, MessageCircle, Phone, PhoneCall, Mail, Trash2, ChevronUp, ChevronDown, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, Loader, AlertTriangle, AlertCircle, Upload, Download, CheckCircle2, XCircle, Clock, ShieldCheck } from 'lucide-react';
import { Skeleton, Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem, Toast, DatePicker } from '../ui';
import { getWhatsappLink, formatDate } from '../../utils/helpers';
import { ProfileModal } from '../modals';
import ProfileDetailModal from '../modals/ProfileDetailModal';
import LogCallModal from '../modals/LogCallModal';
import ApproveProfileModal from '../modals/ApproveProfileModal';
import RejectProfileModal from '../modals/RejectProfileModal';
import { useLanguage } from '../../contexts/LanguageContext';
import CsvSetupModal from '../modals/CsvSetupModal';
import CsvImportModal from '../modals/CsvImportModal';
import { useCsvUpload } from '../../hooks/useCsvUpload';
import { sourcesService, contactService, userService } from '../../services/api';
import { APPROVAL_STATUS_CONFIG } from '../../lib/constants';
import * as XLSX from 'xlsx';

export default function ProfilesView({ profiles, currentUser, loading, onAddProfile, onUpdateProfile, onDeleteProfile, onDeleteAllProfiles, searchProfiles, fetchAllProfiles, pagination, onConvertProfile }) {
  const { t } = useLanguage();
  // URL State
  const [searchParams, setSearchParams] = useSearchParams();
  const showModal = searchParams.get('modal') === 'new-profile';

  const selectedContactId = searchParams.get('contactId');
  const isEditing = searchParams.get('modal') === 'edit-profile'

  const selectedContact = useMemo(() => {
    if (!isEditing || !selectedContactId) return null;
    return profiles.find(c => c.id == selectedContactId) || null;
  }, [profiles, selectedContactId, isEditing]);

  const setShowModal = (show) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (show) newParams.set('modal', 'new-profile');
      else if (newParams.get('modal') === 'new-profile') newParams.delete('modal');
      return newParams;
    });
  };

  const setSelectedContact = (profile) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      if (profile) {
        newParams.set('modal', 'edit-profile');
        newParams.set('contactId', profile.id);
      } else {
        if (newParams.get('modal') === 'edit-profile') newParams.delete('modal');
        newParams.delete('contactId');
      }
      return newParams;
    });
  };
  const [viewContact, setViewContact] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('All');
  const [approvalFilter, setApprovalFilter] = useState('All');
  const [approveModalProfile, setApproveModalProfile] = useState(null);
  const [rejectModalProfile, setRejectModalProfile] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [sorting, setSorting] = useState([]);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [followUpProfile, setFollowUpProfile] = useState(null);
  const [showImportSetup, setShowImportSetup] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [{ pageIndex, pageSize }, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: parseInt(localStorage.getItem('contactsPageSize')) || 10,
  });

  const [dbSources, setDbSources] = useState([]);

  React.useEffect(() => {
    localStorage.setItem('contactsPageSize', pageSize);
  }, [pageSize]);

  React.useEffect(() => {
    sourcesService.getAll()
      .then(res => {
        if (Array.isArray(res)) {
          setDbSources(res.map(s => s.name).filter(Boolean));
        }
      })
      .catch(err => console.error('Error fetching sources for profiles filter:', err));

    userService.getAll()
      .then(res => {
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        setUsersList(list);
      })
      .catch(err => console.error('Error fetching users:', err));
  }, []);

  const { importProgress, closeImportModal, handleFileUpload } = useCsvUpload('profiles', () => {
    if (fetchAllProfiles) fetchAllProfiles({ page: pageIndex + 1, limit: pageSize, search: globalFilter, source: sourceFilter, approvalStatus: approvalFilter, from: startDate, to: endDate });
  });

  const handleApproveProfile = async ({ assignedToId, remarks }) => {
    if (!approveModalProfile) return;
    try {
      await contactService.approve(approveModalProfile.id, { assignedToId, remarks });
      setToast({ message: 'Profile approved successfully!', type: 'success' });
      if (fetchAllProfiles) {
        fetchAllProfiles({
          page: pageIndex + 1,
          limit: pageSize,
          search: globalFilter,
          source: sourceFilter,
          approvalStatus: approvalFilter,
          from: startDate,
          to: endDate
        });
      }
    } catch (err) {
      setToast({ message: err.message || 'Failed to approve profile', type: 'error' });
      throw err;
    }
  };

  const handleRejectProfile = async (remarks) => {
    if (!rejectModalProfile) return;
    try {
      await contactService.reject(rejectModalProfile.id, remarks);
      setToast({ message: 'Profile rejected and returned to creator.', type: 'success' });
      if (fetchAllProfiles) {
        fetchAllProfiles({
          page: pageIndex + 1,
          limit: pageSize,
          search: globalFilter,
          source: sourceFilter,
          approvalStatus: approvalFilter,
          from: startDate,
          to: endDate
        });
      }
    } catch (err) {
      setToast({ message: err.message || 'Failed to reject profile', type: 'error' });
      throw err;
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    
    if (confirmDelete.type === 'single') {
        try {
            if (onDeleteProfile) await onDeleteProfile(confirmDelete.id);
            setToast({ message: t('profileDeleted') || 'Client deleted successfully.', type: 'success' });
        } catch (error) {
            setToast({ message: error.response?.data?.message || error.message || 'Failed to delete profile', type: 'error' });
        }
    } else if (confirmDelete.type === 'all') {
        try {
            const result = await onDeleteAllProfiles({ page: pageIndex + 1, limit: pageSize, search: globalFilter, source: sourceFilter });
            // Use backend's actual message which mentions how many were deleted and if any were preserved
            const msg = result?.message || t('allProfilesDeleted') || 'All clients deleted successfully.';
            setToast({ message: msg, type: 'success' });
        } catch (error) {
            setToast({ message: error.response?.data?.message || error.message || 'Failed to delete profiles', type: 'error' });
        }
    }
    setConfirmDelete(null);
  };

  // Server-side Pagination/Search Trigger
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (fetchAllProfiles) {
        fetchAllProfiles({
          page: pageIndex + 1,
          limit: pageSize,
          search: globalFilter,
          source: sourceFilter,
          approvalStatus: approvalFilter,
          from: startDate,
          to: endDate
        });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [globalFilter, sourceFilter, approvalFilter, startDate, endDate, pageIndex, pageSize, fetchAllProfiles]);

  const availableSources = useMemo(() => {
    const dynamicSources = (profiles || []).map(c => c.source).filter(Boolean);
    return Array.from(new Set([...dbSources, 'Manual Entry', ...dynamicSources]));
  }, [dbSources, profiles]);

  const getProfileStatusLabel = (status) => {
    if (!status || status === 'New') return t('stageNew') || t('new') || 'New';
    if (status === 'Interested') return t('stageInterested') || 'Interested';
    if (status === 'Hold') return t('stageHold') || 'Hold';
    if (status === 'Lost') return t('stageLost') || t('lost') || 'Lost';
    if (status === 'In Process') return t('stageInProcess') || 'In Process';
    return status;
  };

  // Define columns for the table
  const columns = useMemo(() => [
    {
      accessorKey: 'projectName',
      header: t('projectName') || 'Project Name',
      cell: ({ getValue }) => (
        <span className="text-blue-600 font-semibold whitespace-nowrap">{getValue() || '-'}</span>
      ),
    },
    {
      id: 'name',
      accessorFn: row => row.clientName || row.contactPersonName || '',
      header: t('clientName') || 'Client Name',
      cell: ({ row }) => {
        const displayName = row.original.clientName || row.original.contactPersonName || 'No Name';
        return (
          <span className="font-semibold text-gray-900 whitespace-nowrap">
            {displayName}
          </span>
        );
      },
    },
    {
      accessorKey: 'mobile',
      header: t('mobile') || 'Mobile',
      cell: ({ row }) => {
        const number = row.original.mobile || '-';
        return <span className="font-mono text-gray-600 whitespace-nowrap">{number}</span>;
      },
    },
    {
      accessorKey: 'email',
      header: t('email'),
      cell: ({ getValue }) => (
        <span className="text-gray-500 truncate max-w-[180px] block">{getValue() || '-'}</span>
      ),
    },
    {
      accessorKey: 'approvalStatus',
      header: t('approval') || 'Approval',
      cell: ({ row }) => {
        const val = row.original.approvalStatus || 'APPROVED';
        const config = APPROVAL_STATUS_CONFIG[val] || APPROVAL_STATUS_CONFIG['APPROVED'];
        const isPending = val === 'PENDING_APPROVAL';
        const isRejected = val === 'REJECTED';

        return (
          <div className="flex flex-col items-start gap-1">
            <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap w-fit ${config.badge}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${
                val === 'APPROVED' ? 'bg-emerald-500' :
                isPending ? 'bg-amber-500 animate-pulse' :
                'bg-rose-500'
              }`} />
              {t(config.translationKey) || config.label}
            </span>
            {row.original.assignedTo && (
              <span className="text-[11px] text-gray-500 truncate flex items-center gap-1" title={`Assigned to: ${row.original.assignedTo.username}`}>
                <User className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="truncate">{row.original.assignedTo.username}</span>
              </span>
            )}
            {!row.original.assignedTo && row.original.createdBy && (
              <span className="text-[11px] text-gray-400 truncate flex items-center gap-1" title={`Created by: ${row.original.createdBy.username}`}>
                <User className="w-3 h-3 text-gray-400 shrink-0" />
                <span className="truncate">{row.original.createdBy.username}</span>
              </span>
            )}
            {isRejected && row.original.approvalRemarks && (
              <span className="text-[10px] text-rose-600 truncate max-w-[140px] flex items-center gap-1" title={`Reason: ${row.original.approvalRemarks}`}>
                <AlertTriangle className="w-3 h-3 text-rose-500 shrink-0" />
                <span className="truncate">{row.original.approvalRemarks}</span>
              </span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: t('status') || 'Status',
      cell: ({ getValue }) => {
        const status = getValue() || 'New';
        const colorClass = 
          status === 'Interested' ? 'bg-blue-100 text-blue-800' :
          status === 'Hold' ? 'bg-amber-100 text-amber-800' :
          status === 'Lost' ? 'bg-red-100 text-red-800' :
          status.includes('Ready') ? 'bg-emerald-100 text-emerald-800' :
          status === 'In Process' ? 'bg-purple-100 text-purple-800' :
          'bg-gray-100 text-gray-800';
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${colorClass}`}>
            {getProfileStatusLabel(status)}
          </span>
        );
      },
    },
    {
      accessorKey: 'source',
      header: t('source') || 'Source',
      cell: ({ getValue }) => {
        const source = getValue() || 'Manual Entry';
        const isMeta = source.toLowerCase().includes('meta') || source.toLowerCase().includes('facebook');
        const isCsv = source.toLowerCase().includes('csv') || source.toLowerCase().includes('import');
        const isWa = source.toLowerCase().includes('whatsapp');
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
            isMeta ? 'bg-blue-100 text-blue-800' :
            isCsv ? 'bg-purple-100 text-purple-800' :
            isWa ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {source === 'Manual Entry' ? (t('manualEntry') || 'Manual Entry') : source}
          </span>
        );
      },
    },
    {
      accessorKey: 'createdAt',
      header: t('created'),
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <span className="text-gray-500 whitespace-nowrap">
            {formatDate(date, {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </span>
        );
      },
    },
    {
      accessorKey: 'nextCallDate',
      header: t('nextFollowUp') || 'Follow Up Date',
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return <span className="text-gray-400 text-xs">-</span>;
        return (
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">
            <Clock className="w-3 h-3 text-blue-500 shrink-0" />
            {formatDate(date, {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: t('actions'),
      cell: ({ row }) => {
        const isPending = row.original.approvalStatus === 'PENDING_APPROVAL';
        const isApprover = ['CEO', 'GM_Operations', 'BM_Operations'].includes(currentUser?.role);

        return (
          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {/* Quick Approver Actions */}
            {isApprover && isPending && (
              <>
                <button
                  onClick={() => setApproveModalProfile(row.original)}
                  className="px-2.5 py-1 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-lg transition-colors flex items-center gap-1 font-semibold text-xs border border-emerald-200 shadow-sm"
                  title="Approve & Assign Profile"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  <span>{t('approve') || 'Approve'}</span>
                </button>
                <button
                  onClick={() => setRejectModalProfile(row.original)}
                  className="px-2.5 py-1 bg-rose-50 text-rose-700 hover:bg-rose-100 rounded-lg transition-colors flex items-center gap-1 font-semibold text-xs border border-rose-200 shadow-sm"
                  title="Reject Profile with Comments"
                >
                  <XCircle className="w-3.5 h-3.5" />
                  <span>{t('reject') || 'Reject'}</span>
                </button>
              </>
            )}

            <button
              onClick={() => setFollowUpProfile(row.original)}
              className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
              title={t('logCall') || "Log Call / Follow up"}
            >
              <PhoneCall className="w-4 h-4" />
            </button>
            <button
              onClick={() => setConfirmDelete({ type: 'single', id: row.original.id })}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title={t('delete')}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        );
      },
      enableSorting: false,
    },
  ], [onDeleteProfile, currentUser, t]);

  // Initialize the table
  const table = useReactTable({
    data: profiles,
    columns,
    pageCount: pagination?.totalPages || -1,
    state: {
      globalFilter,
      sorting,
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    manualPagination: true,
    onPaginationChange: setPaginationState,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleSave = async (data) => {
    await onAddProfile(data);
    setShowModal(false);
  };

  const handleUpdate = async (data) => {
    if (selectedContact && selectedContact.id) {
      await onUpdateProfile(selectedContact.id, data);
    }
    setSelectedContact(null);
  };

  const exportToExcel = async () => {
    try {
      const res = await contactService.getAll({
        page: 1,
        limit: 100000,
        search: globalFilter,
        source: sourceFilter === 'All' ? '' : sourceFilter,
        from: startDate,
        to: endDate
      });
      const allProfiles = res.data || [];
      const headers = [
        t('projectName') || 'Project Name', 
        t('clientName') || 'Client Name', 
        t('mobile') || 'Mobile', 
        t('email') || 'Email', 
        t('source') || 'Source', 
        t('created') || 'Created At',
        t('nextFollowUp') || 'Follow Up Date'
      ];
      const formatDateDMY = (val) => {
        if (!val) return '';
        const str = String(val).trim();
        if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(str)) return str;
        const d = new Date(str);
        if (isNaN(d.getTime())) return str;
        const day = String(d.getUTCDate()).padStart(2, '0');
        const month = String(d.getUTCMonth() + 1).padStart(2, '0');
        const year = d.getUTCFullYear();
        return `${day}-${month}-${year}`;
      };
      const aoa = [headers, ...allProfiles.map(p => [
        p.projectName || '',
        p.clientName || p.contactPersonName || '',
        String(p.mobile || ''),
        p.email || '',
        p.source || 'Manual Entry',
        formatDateDMY(p.createdAt),
        formatDateDMY(p.nextCallDate)
      ])];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      ws['!cols'] = [20, 20, 15, 25, 15, 15, 16].map(w => ({ wch: w }));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Profiles');
      XLSX.writeFile(wb, `profiles_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (err) {
      console.error('Error exporting profiles:', err);
      setToast({ message: t('exportFailed') || 'Failed to export profiles', type: 'error' });
    }
  };

  return (
    <div className="space-y-4 animate-fade-in">
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Add New Profile Modal */}
      {showModal && <ProfileModal onClose={() => setShowModal(false)} onSave={handleSave} />}

      {/* Approve Profile Modal */}
      {approveModalProfile && (
        <ApproveProfileModal
          profile={approveModalProfile}
          users={usersList}
          onConfirm={handleApproveProfile}
          onClose={() => setApproveModalProfile(null)}
        />
      )}

      {/* Reject Profile Modal */}
      {rejectModalProfile && (
        <RejectProfileModal
          profile={rejectModalProfile}
          onConfirm={handleRejectProfile}
          onClose={() => setRejectModalProfile(null)}
        />
      )}

      {/* Profile Detail Modal (opens on row click) */}
      {viewContact && (
        <ProfileDetailModal
          profile={viewContact}
          currentUser={currentUser}
          users={usersList}
          onClose={() => setViewContact(null)}
          onSave={(updated) => {
            if (onUpdateProfile) onUpdateProfile(updated);
            setViewContact(updated);
          }}
          onRefresh={fetchAllProfiles}
        />
      )}

      {/* Edit Profile Modal (opens from Edit button if added later, but for now we just keep this state for direct edits if any) */}
      {selectedContact && (
        <ProfileModal
          onClose={() => setSelectedContact(null)}
          onSave={handleUpdate}
          initialData={selectedContact}
        />
      )}

      {/* Header */}
      <div className="mb-2">
        <h2 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600">{t('profilesDirectory')}</h2>
        <p className="text-gray-500 mt-1">{(pagination?.absoluteTotal ?? pagination?.total) || 0} {t('totalProfiles')}</p>
      </div>

      {/* Approval Status Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-gray-200 pb-2 overflow-x-auto">
        {[
          { id: 'All', label: t('allProfiles') || 'All Profiles' },
          { id: 'PENDING_APPROVAL', label: t('pendingApproval') || 'Pending Approval', isPending: true },
          { id: 'APPROVED', label: t('approved') || 'Approved' },
          { id: 'REJECTED', label: t('rejected') || 'Rejected' },
        ].map((tab) => {
          const isActive = approvalFilter === tab.id;
          const pendingCount = (profiles || []).filter(p => p.approvalStatus === 'PENDING_APPROVAL').length;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setApprovalFilter(tab.id);
                setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 whitespace-nowrap ${
                isActive
                  ? tab.isPending
                    ? 'bg-amber-500 text-white shadow-sm'
                    : tab.id === 'APPROVED'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : tab.id === 'REJECTED'
                    ? 'bg-rose-600 text-white shadow-sm'
                    : 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {tab.isPending && (
                <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-white' : 'bg-amber-500'} ${pendingCount > 0 ? 'animate-ping' : ''}`} />
              )}
              <span>{tab.label}</span>
              {tab.isPending && pendingCount > 0 && (
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] font-bold ${
                  isActive ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-800'
                }`}>
                  {pendingCount}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Toolbar */}
      <div className="flex flex-col lg:flex-row justify-between gap-3 bg-white p-4 md:p-5 rounded-xl border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              placeholder={t('searchProfiles')}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />
          </div>
          <Dropdown className="w-full sm:w-auto">
            <DropdownTrigger>
              <button
                className="w-full sm:w-auto min-w-[150px] bg-white hover:bg-slate-50 border border-gray-200 rounded-lg text-sm px-3.5 h-[42px] focus:ring-2 focus:ring-blue-500 outline-none flex justify-between items-center text-left shadow-sm transition-colors font-medium text-gray-700"
              >
                <span className="truncate">{sourceFilter === 'All' ? (t('allSources') || 'All Sources') : sourceFilter}</span>
                <ChevronDown className="w-4 h-4 text-gray-400 shrink-0 ml-2" />
              </button>
            </DropdownTrigger>
            <DropdownPopover className="w-[180px] max-h-60 overflow-y-auto">
              <DropdownMenu
                selectedKeys={new Set([sourceFilter])}
                selectionMode="single"
                onSelectionChange={(keys) => {
                  setSourceFilter(Array.from(keys)[0]);
                  setPaginationState(prev => ({ ...prev, pageIndex: 0 }));
                }}
              >
                <DropdownItem id="All">{t('allSources') || 'All Sources'}</DropdownItem>
                {availableSources.map((src) => (
                  <DropdownItem key={src} id={src}>
                    {src === 'Manual Entry' ? (t('manualEntry') || 'Manual Entry') : src}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </DropdownPopover>
          </Dropdown>

          {/* Date range pickers */}
          <div className="flex items-center gap-1.5 w-full sm:w-auto">
            <DatePicker
              className="flex-1 sm:flex-none sm:w-[130px]"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              fullWidth={false}
            />
            <span className="text-gray-400 font-medium shrink-0">–</span>
            <DatePicker
              className="flex-1 sm:flex-none sm:w-[130px]"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              fullWidth={false}
            />
            {(startDate || endDate) && (
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="text-xs font-semibold text-red-500 px-2 h-[42px] hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
              >
                {t('clear') || 'Clear'}
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 w-full lg:w-auto">
          <button
            onClick={exportToExcel}
            className="flex-1 sm:flex-none justify-center h-[42px] bg-sky-50 text-sky-700 border border-sky-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-sky-100 transition-colors whitespace-nowrap shadow-sm"
          >
            <Download className="w-4 h-4" /> <span className="hidden sm:inline">{t('exportExcel') || 'Export'}</span>
          </button>
          <button
            onClick={() => setShowImportSetup(true)}
            className="flex-1 sm:flex-none justify-center h-[42px] bg-emerald-50 text-emerald-700 border border-emerald-200 px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-emerald-100 transition-colors whitespace-nowrap shadow-sm"
          >
            <Upload className="w-4 h-4" /> <span>{t('importCsv')}</span>
          </button>
          {onDeleteAllProfiles && (
            <button
              onClick={() => setConfirmDelete({ type: 'all' })}
              className="flex-none justify-center h-[42px] bg-red-600 text-white px-3 sm:px-4 rounded-lg text-sm font-semibold flex items-center gap-1.5 hover:bg-red-700 transition-colors whitespace-nowrap shadow-sm"
              title={t('deleteAll')}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">{t('deleteAll')}</span>
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="w-full sm:w-auto flex-1 sm:flex-none justify-center h-[42px] bg-blue-600 text-white px-4 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors whitespace-nowrap shadow-sm"
          >
            <UserPlus className="w-4 h-4" />
            <span>{t('addProfile')}</span>
          </button>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className={`lg:hidden space-y-2 ${loading ? 'opacity-70 pointer-events-none' : ''}`}>
        {loading && profiles.length === 0 ? (
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <Skeleton width={120} height={16} animation="wave" />
                    <Skeleton width={100} height={12} animation="wave" />
                  </div>
                </div>
                <div className="pt-3 border-t flex justify-between">
                  <Skeleton width={80} height={14} animation="wave" />
                  <Skeleton width={60} height={14} animation="wave" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          table.getRowModel().rows.map((row) => (
            <div
              key={row.id}
              onClick={() => setViewContact(row.original)}
              className="bg-white p-4 rounded-xl border shadow-sm active:bg-gray-50 cursor-pointer"
            >
              <div className="flex justify-between items-start">
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-gray-900">
                    {row.original.clientName || row.original.contactPersonName || 'No Name'}
                  </p>
                  <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                    <Phone className="w-3 h-3" /> {row.original.mobile || '-'}
                  </p>
                  {row.original.email && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5 truncate">
                      <Mail className="w-3 h-3" /> {row.original.email}
                    </p>
                  )}
                  <div className="mt-2 flex gap-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">
                      {row.original.source === 'Manual Entry' ? (t('manualEntry') || 'Manual Entry') : (row.original.source || t('manualEntry') || 'Manual Entry')}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
                      (row.original.status || 'New') === 'Interested' ? 'bg-blue-100 text-blue-800' :
                      (row.original.status || 'New') === 'Hold' ? 'bg-amber-100 text-amber-800' :
                      (row.original.status || 'New') === 'Lost' ? 'bg-red-100 text-red-800' :
                      (row.original.status || 'New').includes('Ready') ? 'bg-emerald-100 text-emerald-800' :
                      (row.original.status || 'New') === 'In Process' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getProfileStatusLabel(row.original.status || 'New')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
        {!loading && table.getRowModel().rows.length === 0 && (
          <div className="text-center py-8 text-gray-400">{t('noProfiles')}</div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className={`hidden lg:block bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden ${loading ? 'opacity-70 pointer-events-none' : ''}`}>
        {loading && profiles.length === 0 ? (
          <div className="p-4 space-y-4">
            <div className="flex items-center gap-4 border-b pb-3">
              {[1, 2, 3, 4, 5, 6].map(i => <Skeleton key={i} width={100} height={16} animation="wave" />)}
            </div>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 py-2 border-b last:border-0">
                <Skeleton width={150} height={16} animation="wave" />
                <Skeleton width={150} height={14} animation="wave" />
                <Skeleton width={100} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
                <Skeleton width={80} height={14} animation="wave" />
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-50 to-gray-50 border-b border-gray-200">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`px-4 py-3.5 text-left font-semibold text-gray-600 whitespace-nowrap ${header.column.getCanSort() ? 'cursor-pointer select-none hover:bg-gray-100 transition-colors' : ''
                          }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="flex items-center gap-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="text-gray-400">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <div className="w-4 h-4 opacity-0 group-hover:opacity-50">
                                  <ChevronUp className="w-4 h-4" />
                                </div>
                              )}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {table.getRowModel().rows.map((row) => (
                  <tr 
                    key={row.id} 
                    className="hover:bg-blue-50/50 transition-colors cursor-pointer"
                    onClick={() => setViewContact(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3.5">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Empty State */}
        {!loading && table.getRowModel().rows.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium">{t('noProfiles')}</p>
            <p className="text-sm mt-1">{t('addFirstProfile')}</p>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {
        profiles.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">{t('show')}</span>
              <Dropdown>
                <DropdownTrigger>
                  <button className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 flex justify-between items-center w-[70px]">
                    {table.getState().pagination.pageSize}
                    <ChevronDown className="w-3 h-3 text-gray-400 shrink-0 ml-1" />
                  </button>
                </DropdownTrigger>
                <DropdownPopover className="w-[70px]" placement="top">
                  <DropdownMenu
                    selectedKeys={new Set([table.getState().pagination.pageSize.toString()])}
                    selectionMode="single"
                    onSelectionChange={(keys) => table.setPageSize(Number(Array.from(keys)[0]))}
                  >
                    {[10, 20, 50].map((pageSize) => (
                      <DropdownItem key={pageSize} id={pageSize.toString()}>
                        {pageSize}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </DropdownPopover>
              </Dropdown>
              <span className="text-sm text-gray-600">{t('perPage')}</span>
            </div>

            {/* Page Info */}
            <div className="text-sm text-gray-600">
              {t('page')}{' '}
              <span className="font-semibold text-gray-900">
                {table.getState().pagination.pageIndex + 1}
              </span>{' '}
              {t('of')}{' '}
              <span className="font-semibold text-gray-900">
                {table.getPageCount()}
              </span>
              {' \u00B7 '}
              <span className="text-gray-500">
                {pagination?.total || 0} {t('results')}
              </span>
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => table.setPageIndex(0)}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="First page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Previous page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {/* Page Number Buttons */}
              <div className="hidden sm:flex items-center gap-1 mx-2">
                {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
                  const currentPage = table.getState().pagination.pageIndex;
                  const totalPages = table.getPageCount();
                  let pageNum;

                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (currentPage <= 2) {
                    pageNum = i;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => table.setPageIndex(pageNum)}
                      className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-200 hover:bg-gray-50 text-gray-700'
                        }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Next page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                disabled={!table.getCanNextPage()}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Last page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )
      }



      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Confirmation Modal */}
      {confirmDelete && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
          onClick={() => setConfirmDelete(null)}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center animate-scale-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-16 h-16 bg-red-100 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {confirmDelete.type === 'all' ? t('confirmDeleteAllTitle') || 'Delete All Profiles?' : t('confirmDeleteTitle') || 'Delete Profile?'}
            </h3>
            <p className="text-gray-500 text-sm mb-4">
              {confirmDelete.type === 'all' 
                ? t('confirmDeleteAll') || 'Are you sure you want to delete all profiles? This action cannot be undone.'
                : t('confirmDelete') || 'Are you sure you want to delete this profile? This action cannot be undone.'}
            </p>
            <div className="bg-orange-50 border border-orange-100 rounded-lg p-3 flex gap-2 items-start text-left mb-6">
              <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <p className="text-orange-700 text-xs leading-relaxed">
                {t('confirmDeleteWarning') || 'Note: Profiles linked to existing prequalifications will not be deleted.'}
              </p>
            </div>
            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-5 py-2.5 rounded-xl font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                {t('cancel') || 'Cancel'}
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-5 py-2.5 rounded-xl font-semibold text-white bg-red-600 hover:bg-red-700 transition-colors shadow-sm shadow-red-200"
              >
                {t('delete') || 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && (
        <ProfileModal
          onClose={() => setShowModal(false)}
          onSave={onAddProfile}
        />
      )}
      {selectedContact && (
        <ProfileModal
          initialData={selectedContact}
          onClose={() => setSelectedContact(null)}
          onSave={(id, data) => onUpdateProfile(id, data)}
        />
      )}
      {followUpProfile && (
        <LogCallModal
          prequalification={followUpProfile}
          isProfile={true}
          currentUser={currentUser}
          onClose={() => setFollowUpProfile(null)}
          onSave={async (id, payload) => {
            try {
              await contactService.logCall(id, payload);
              if (fetchAllProfiles) fetchAllProfiles({ page: pageIndex + 1, limit: pageSize, search: globalFilter, source: sourceFilter, from: startDate, to: endDate });
              setFollowUpProfile(null);
              setToast({ message: 'Call logged successfully', type: 'success' });
            } catch (err) {
              setToast({ message: err.message || 'Failed to log call', type: 'error' });
            }
          }}
          onSaveLead={async (id, payload) => {
            try {
              await contactService.update(id, payload);
              if (fetchAllProfiles) fetchAllProfiles({ page: pageIndex + 1, limit: pageSize, search: globalFilter, source: sourceFilter, from: startDate, to: endDate });
            } catch (err) {
              throw err;
            }
          }}
          venues={[]}
          eventTypes={[]}
          managers={[]}
          onConvertProfile={onConvertProfile}
        />
      )}

      {/* CSV Modals */}
      <CsvSetupModal 
        isOpen={showImportSetup}
        onClose={() => setShowImportSetup(false)}
        onStartImport={(file) => {
          setShowImportSetup(false);
          handleFileUpload({ target: { files: [file], value: '' } });
        }}
        importType="profiles"
      />

      {importProgress.open && (
        <CsvImportModal progress={importProgress} onClose={closeImportModal} importType="profiles" />
      )}
    </div >
  );
}
